import { modelManager } from '@/editor/manager/entityManager';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { TGlobalStores } from '@/editor/stores';
import { EEntityType, EOperateMode, EToolType } from '@/editor/types';
import { EPatchType, IPatchItem } from '@/editor/types/baseStore';
import { EOperationType } from '@/editor/types/operation';
import { AbstractHistoryStore } from '@/editor/history/abstractHistoryStore';

interface OperationInfo {
  date: number;
  type: EOperationType;
  changes: IPatchItem<unknown>[];
  pointData: ReturnType<TGlobalStores['pointStore']['getData']>;
}

const DEBOUNCE_DELAY = 300; // ms

const MAX_STACK_LENGTH = 20;

const RESET_OPERATE_MODE_WHITE_LIST: readonly EOperateMode[] = [EOperateMode.Alignment];

class History {
  private undoStack: OperationInfo[] = [];
  private redoStack: OperationInfo[] = [];
  private lastUpdateTime: number = Date.now();
  private _currentChanges: IPatchItem<unknown>[] | null = null; // null表示不进行记录
  private _operationCount: number = 0; // startOperation调用次数记录
  private _storeMap: Map<string | number, AbstractHistoryStore> = new Map();
  private _prevOperateMode: EOperateMode = EOperateMode.EMPTY;

  private _lastOperationPointData = new Map<string, Set<number>>();

  private _gs: TGlobalStores;

  constructor(gs: TGlobalStores) {
    this._gs = gs;
  }

  addStore(store: AbstractHistoryStore) {
    this._storeMap.set(store.id, store);
  }

  recordDataChange(item: IPatchItem<unknown>) {
    if (!this._currentChanges) return;
    this._currentChanges.push(item);
  }

  // undoStack最后一项的的最后一个change和_currentChanges的第一项进行对比合并
  mergeUpdateChanges() {
    // 短时间多次调用才进行合并
    const now = Date.now();
    const timeDifference = now - this.lastUpdateTime;
    this.lastUpdateTime = now;
    if (timeDifference > DEBOUNCE_DELAY) return;
    if (!this._currentChanges?.length) return;

    if (this._currentChanges.length > 1) {
      // 先处理未压栈的情况
      const latestChange = this._currentChanges[this._currentChanges.length - 1];
      const secondLatestChange = this._currentChanges[this._currentChanges.length - 2];
      if (
        latestChange.type !== EPatchType.Update ||
        secondLatestChange.type !== EPatchType.Update ||
        secondLatestChange.sId !== latestChange.sId ||
        secondLatestChange.id !== latestChange.id
      ) {
        return;
      }
      // 合并结果替换末尾两项
      this._currentChanges.splice(-2, 2, {
        ...secondLatestChange,
        data: {
          ...latestChange.data,
          ...secondLatestChange.data
        }
      });

      return;
    }

    // 处理已压栈的情况
    const lastOperationInfo = this.undoStack[this.undoStack.length - 1];
    if (!lastOperationInfo || lastOperationInfo.changes.length !== 1) return;

    const lastChangeOfLastOperation = lastOperationInfo.changes[lastOperationInfo.changes.length - 1];
    const latestChange = this._currentChanges[0];

    if (
      lastChangeOfLastOperation.type !== EPatchType.Update ||
      latestChange.type !== EPatchType.Update ||
      lastChangeOfLastOperation.sId !== latestChange.sId ||
      lastChangeOfLastOperation.id !== latestChange.id
    )
      return;

    this._currentChanges.pop();
    lastOperationInfo.changes[lastOperationInfo.changes.length - 1] = {
      ...lastChangeOfLastOperation,
      data: {
        ...latestChange.data,
        ...lastChangeOfLastOperation.data
      }
    };
  }

  // 每次操作前调用，主要用于防止多个操作同时进行，导致数据错误
  startOperation() {
    if (this._operationCount > 0) {
      console.error('history start already');
    }
    this._operationCount++;
  }

  completeOperation(operationType: EOperationType) {
    this._operationCount--;
    if (this._operationCount !== 0) return;

    // 如果当前没有记录，则不压栈
    if (!this._currentChanges || !this._currentChanges.length) return;

    this.redoStack = [];

    if (this.undoStack.length >= MAX_STACK_LENGTH) {
      this.undoStack.shift();
    }

    this.undoStack.push({
      date: Date.now(),
      changes: this._currentChanges || [],
      type: operationType,
      pointData: this._lastOperationPointData
    });
    this._currentChanges = [];
    this._lastOperationPointData = this._gs.pointStore.getData();
    this.lastUpdateTime = Date.now();

    this.emitEvent();
  }

  /**
   * 合并在某个时段下的同类 Operation
   * @todo 需要测试是否会影响历史记录的准确性
   */
  public mergeLastTwoOperationByType(operationType: EOperationType) {
    if (this._operationCount !== 0) return;
    if (this.undoStack.length < 2) return;
    const topOpInfo = this.undoStack[this.undoStack.length - 1];
    const topSecondOpInfo = this.undoStack[this.undoStack.length - 2];

    if (!topOpInfo || !topSecondOpInfo) return;
    if (topOpInfo.type !== operationType) return;
    if (topSecondOpInfo.type !== topOpInfo.type) return;

    const timeDifference = topOpInfo.date - topSecondOpInfo.date;

    if (timeDifference > DEBOUNCE_DELAY) return;

    topSecondOpInfo.changes.push(...topOpInfo.changes);
    topSecondOpInfo.pointData = topOpInfo.pointData;
    topSecondOpInfo.date = Date.now();
    this.undoStack.pop();
  }

  private beforeExecute() {
    this._prevOperateMode = this._gs.operateInfo.mode;
    // 白名单中的operateMode无需重置
    if (!RESET_OPERATE_MODE_WHITE_LIST.includes(this._gs.operateInfo.mode)) {
      this._gs.operateInfo = {
        mode: EOperateMode.EMPTY,
        toolType: EToolType.empty
      };
    }
  }
  private afterExecute() {
    this._handleMultiModelWhenAfterExecute();
    modelManager.updateEntity();
    modelManager.selectedRerender();
    this.emitEvent();

    editorEvent.emit(EditorEventName.REPLACE_REFRESH);
  }

  private _handleMultiModelWhenAfterExecute() {
    if (this._gs.editorStateStore.selected.type === EEntityType.MultiModel) {
      // 撤销不清除对齐模式下的多选
      if (this._prevOperateMode !== EOperateMode.Alignment) {
        this._gs.editorStateStore.clearSelected();
      } else {
        modelManager.alignManager.updateAlignReference();
        this._gs.editorStateStore.refreshFurnitureMultiSelected();
      }
    }
    this._prevOperateMode = EOperateMode.EMPTY;
  }

  // 主要是数据处理
  private execute(mode: 'undo' | 'redo') {
    const popStack = mode === 'undo' ? this.undoStack : this.redoStack;
    const pushStack = mode === 'undo' ? this.redoStack : this.undoStack;

    const operationInfo = popStack.pop();
    if (!operationInfo) return;

    this._currentChanges = []; // 清空当前记录，开始记录逆操作
    const currentPointData = this._gs.pointStore.getData(); // 记录当前点数据，用于恢复
    this._gs.pointStore.coverDatas(operationInfo.pointData); // 恢复到前一次点数据
    operationInfo.changes.reverse().forEach((change) => {
      const store = this._storeMap.get(change.sId);
      // 记录进撤销栈的 change 必定来自已注册的 store，取不到即为程序错误
      if (!store) throw new Error(`历史记录应用失败：未找到 sId 为 ${change.sId} 的 store`);
      store.applyChange(change);
    });
    pushStack.push({
      date: Date.now(),
      type: operationInfo.type,
      changes: this._currentChanges,
      pointData: currentPointData
    });
    this._currentChanges = [];

    this._gs.wallStore.updateAllWallShape();
    this._gs.refreshData();

    this._lastOperationPointData = operationInfo.pointData;
  }

  undo() {
    if (this.undoStack.length === 0) return;

    this.beforeExecute();

    this.execute('undo');

    this.afterExecute();
  }

  redo() {
    if (this.redoStack.length === 0) return;

    this.beforeExecute();

    this.execute('redo');

    this.afterExecute();
  }

  private emitEvent() {
    editorEvent.emit(EditorEventName.HISTORY_CHANGE, {
      undoStackLength: this.undoStack.length,
      redoStackLength: this.redoStack.length
    });
  }

  init() {
    this.clear();
    this._currentChanges = [];
    this._lastOperationPointData = this._gs.pointStore.getData();
    this.emitEvent();
  }

  // 清空数据并停止记录
  clear() {
    this.undoStack = [];
    this.redoStack = [];
    this._currentChanges = null;
    this._lastOperationPointData = new Map<string, Set<number>>();
    this._operationCount = 0;
    this.lastUpdateTime = Date.now();
  }
}

export { History };
